import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import HomePage from './components/HomePage';
import PageUsage from './components/PageUsage';
import PaperDetails from './components/paper-details/PaperDetails';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Navigation from './components/Navigation';
import Terms from './components/Terms';
import RedirectHandler from './components/RedirectHandler';
import { AuthProvider, useAuth } from './context/AuthContext';
import BattleVisualizations from './components/BattleVisualizations';
import About from './components/About';
import PaperSkeleton from './components/paper-details/PaperSkeleton';

// Developer pages, account settings and the OAuth consent screen load lazily so the rest of the
// site doesn't pay for them.
const DevelopersPageClassic = lazy(() => import('./components/developers/classic/DevelopersPageClassic'));
const ApiDocsPage = lazy(() => import('./components/developers/ApiDocsPage'));
const DeveloperSettings = lazy(() => import('./components/account/DeveloperSettings'));
const OAuthConsent = lazy(() => import('./components/oauth/OAuthConsent'));

// Pages render inside the padded content column; the consent screen brings its own chrome.
const PaddedLayout = () => (
  <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <Outlet />
  </div>
);

const AuthStatusWrapper = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <PaperSkeleton />;
  }
  
  return React.cloneElement(children, { isAuthenticated: isAuthenticated() });
};

const App = () => {

  useEffect(() => {
    const buildTime = process.env.REACT_APP_BUILD_TIME;
    const lastBuildTime = localStorage.getItem('build_time');
    
    // No build stamp (local dev) → nothing to compare; reloading would loop forever.
    if (buildTime && lastBuildTime !== buildTime) {
      localStorage.setItem('build_time', buildTime);
      window.location.reload(true);
    }
  }, []);
  
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      const script = document.createElement('script');
      script.innerHTML = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-MZTSV929');
      `;
      document.head.appendChild(script);
    }
  }, []);

  return (
    <AuthProvider>
      <Router>
        <PageUsage />
        <div className="min-h-screen">
          <Navigation />
          <Routes>
            <Route element={<PaddedLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/" element={<HomePage />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/paper/:paperId" element={<AuthStatusWrapper><PaperDetails /></AuthStatusWrapper>} />
              <Route path="/redirect" element={<RedirectHandler />} />
              <Route path="/paper-rankings" element={<BattleVisualizations />} />
              <Route path="/about" element={<About />} />
              <Route path="/developers" element={<Suspense fallback={null}><DevelopersPageClassic /></Suspense>} />
              <Route path="/mcp" element={<Suspense fallback={null}><DevelopersPageClassic focus="mcp" /></Suspense>} />
              <Route path="/developers/classic" element={<Navigate to="/developers" replace />} />
              <Route path="/mcp/classic" element={<Navigate to="/mcp" replace />} />
              <Route path="/developers/docs" element={<Suspense fallback={null}><ApiDocsPage /></Suspense>} />
              <Route path="/account/developer" element={<Suspense fallback={null}><DeveloperSettings /></Suspense>} />
            </Route>
            <Route
              path="/oauth/authorize"
              element={
                <Suspense fallback={<div className="min-h-screen bg-gray-100" aria-busy="true" />}>
                  <OAuthConsent />
                </Suspense>
              }
            />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;